// ./src/components/features/Alerts/AlertItem/AlertItem.tsx
import React from "react";
import { Group, Stack, Text, Badge, UnstyledButton, Box } from "@mantine/core";

import { getRelativeTime } from "@/utils/dateHelpers";
import { UserNotification } from "@/utils/api/UserNotificationService";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export type AlertSeverity = "info" | "warning" | "error" | "success";

export interface AlertItemProps {
  /** Notification object */
  notification: UserNotification;

  /** Click handler */
  onClick?: (notification: UserNotification) => void;

  /** Whether to show the timestamp */
  showTimestamp?: boolean;

  /** Whether the alert is read */
  isRead?: boolean;

  /** Size variant */
  size?: "sm" | "md";

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const SEVERITY_CONFIG: Record<
  AlertSeverity,
  { iconName: string; color: string; bgColor: string }
> = {
  info: {
    iconName: "InfoCircle",
    color: "blue",
    bgColor: "var(--mantine-color-blue-0)",
  },
  warning: {
    iconName: "AlertTriangle",
    color: "yellow",
    bgColor: "var(--mantine-color-yellow-0)",
  },
  error: {
    iconName: "AlertCircle",
    color: "red",
    bgColor: "var(--mantine-color-red-0)",
  },
  success: {
    iconName: "CircleCheck",
    color: "green",
    bgColor: "var(--mantine-color-green-0)",
  },
};

const SIZE_CONFIG = {
  sm: {
    iconSize: 18,
    titleSize: "sm" as const,
    descSize: "xs" as const,
    padding: "xs" as const,
    gap: "xs" as const,
  },
  md: {
    iconSize: 22,
    titleSize: "sm" as const,
    descSize: "sm" as const,
    padding: "sm" as const,
    gap: "sm" as const,
  },
};

// Notification type to severity mapping
const getNotificationSeverity = (
  notification: UserNotification
): AlertSeverity => {
  const type = (notification as any).type?.toLowerCase() || "";
  const priority = (notification as any).priority?.toLowerCase() || "";

  if (type.includes("error") || priority === "high") {
    return "error";
  }
  if (
    type.includes("warning") ||
    type.includes("maintenance") ||
    type.includes("reminder")
  ) {
    return "warning";
  }
  if (type.includes("success")) {
    return "success";
  }
  return "info";
};

// ============================================
// Component
// ============================================

/**
 * Displays a single alert/notification item.
 * Shows icon, title, description, timestamp, and optional badge.
 */
export const AlertItem: React.FC<AlertItemProps> = ({
  notification,
  onClick,
  showTimestamp = true,
  isRead,
  size = "md",
  className,
}) => {
  const config = SIZE_CONFIG[size];
  const severity = getNotificationSeverity(notification);
  const severityConfig = SEVERITY_CONFIG[severity];

  // Extract notification fields
  const title =
    (notification as any).title ||
    (notification as any).subject ||
    "Notification";
  const description =
    (notification as any).message || (notification as any).body || "";
  const timestamp =
    (notification as any).createdAt || (notification as any).sentAt;
  const read =
    isRead ??
    (notification as any).isRead ??
    (notification as any).readAt != null;

  const handleClick = () => {
    if (onClick) {
      onClick(notification);
    }
  };

  const content = (
    <Group
      gap={config.gap}
      wrap="nowrap"
      align="flex-start"
      p={config.padding}
      className={className}
      style={{
        backgroundColor: read ? "transparent" : severityConfig.bgColor,
        borderRadius: "var(--mantine-radius-md)",
        transition: "background-color 150ms ease",
      }}
    >
      {/* Icon */}
      <Box
        style={{
          color: `var(--mantine-color-${severityConfig.color}-6)`,
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <AppIcon
          iconName={severityConfig.iconName}
          size={config.iconSize}
          stroke={1.5}
        />
      </Box>

      {/* Content */}
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Text
            size={config.titleSize}
            fw={read ? 400 : 600}
            truncate
            style={{ flex: 1, minWidth: 0 }}
          >
            {title}
          </Text>

          {!read && (
            <Badge size="xs" variant="dot" color={severityConfig.color}>
              New
            </Badge>
          )}
        </Group>

        {description && (
          <Box
            c="dimmed"
            fz={config.descSize}
            style={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
            }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        {showTimestamp && timestamp && (
          <Text size="xs" c="dimmed">
            {getRelativeTime(timestamp)}
          </Text>
        )}
      </Stack>
    </Group>
  );

  // Wrap in button if clickable
  if (onClick) {
    return (
      <UnstyledButton
        onClick={handleClick}
        style={{
          display: "block",
          width: "100%",
          borderRadius: "var(--mantine-radius-md)",
        }}
        styles={{
          root: {
            "&:hover": {
              backgroundColor: "var(--mantine-color-gray-0)",
            },
          },
        }}
      >
        {content}
      </UnstyledButton>
    );
  }

  return content;
};
